import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { tasksApi, projectsApi, taskTagsApi } from '../utils/api';
import type { TaskListItem, TaskTagTreeNode } from '../types/task';
import { TaskStatus } from '../types/task';
import type { ProjectListItem } from '../types/project';
import { useAuth } from '../utils/auth';
import { useWebSocket } from '../utils/WebSocketContext';
import Navigation from '../components/Navigation';
import KanbanBoard from '../components/KanbanBoard';
import { useSmartSticky } from '../hooks/useSmartSticky';

// Tag Tree Node Component
interface TagTreeNodeProps {
  node: TaskTagTreeNode;
  level: number;
  selectedTagId: number | null;
  onSelectTag: (tagId: number | null) => void;
}

function TagTreeNode({ node, level, selectedTagId, onSelectTag }: TagTreeNodeProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const hasChildren = node.children && node.children.length > 0;
  const isSelected = selectedTagId === node.id;

  return (
    <div>
      <div
        className={`flex items-center py-1.5 px-2 rounded cursor-pointer hover:bg-gray-100 ${
          isSelected ? 'bg-gray-200 font-medium' : ''
        }`}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        onClick={() => onSelectTag(isSelected ? null : node.id)}
      >
        {hasChildren ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            className="mr-1.5 w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600"
          >
            {isExpanded ? '▼' : '▶'}
          </button>
        ) : (
          <span className="mr-1.5 w-4" />
        )}

        {node.color && (
          <span
            className="w-3 h-3 rounded-full mr-1.5 flex-shrink-0"
            style={{ backgroundColor: node.color }}
          />
        )}

        <span className="flex-1 text-sm truncate">{node.name}</span>
        <span className="text-xs text-gray-400 ml-1">{node.task_count}</span>
      </div>

      {hasChildren && isExpanded && (
        <div>
          {node.children.map((child) => (
            <TagTreeNode
              key={child.id}
              node={child}
              level={level + 1}
              selectedTagId={selectedTagId}
              onSelectTag={onSelectTag}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const getStatusColor = (status: TaskStatus): string => {
  switch (status) {
    case TaskStatus.DRAFT:
      return 'bg-yellow-100 text-yellow-700';
    case TaskStatus.PENDING:
      return 'bg-gray-100 text-gray-700';
    case TaskStatus.PROCESSING:
      return 'bg-blue-100 text-blue-700';
    case TaskStatus.COMPLETED:
      return 'bg-green-100 text-green-700';
    case TaskStatus.FAILED:
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const getStatusLabel = (status: TaskStatus): string => {
  return status.charAt(0).toUpperCase() + status.slice(1);
};

export default function TasksPage() {
  const { user } = useAuth();
  const { lastUpdate } = useWebSocket();
  const [tasks, setTasks] = useState<TaskListItem[]>([]);
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [tagTree, setTagTree] = useState<TaskTagTreeNode[]>([]);

  // Smart sticky sidebar
  const sidebarRef = useSmartSticky(96, 16); // 96px from top (nav), 16px from bottom
  const [selectedTagId, setSelectedTagId] = useState<number | null>(null);
  const [filteredStatus, setFilteredStatus] = useState<string>(() => {
    return localStorage.getItem('tasksFilteredStatus') || '';
  });
  const [userFilter, setUserFilter] = useState<string>(() => {
    return localStorage.getItem('tasksUserFilter') || 'my';
  });
  const [projectFilter, setProjectFilter] = useState<string>(() => {
    return localStorage.getItem('tasksProjectFilter') || '';
  });
  const [viewMode, setViewMode] = useState<'list' | 'kanban'>(() => {
    return (localStorage.getItem('tasksViewMode') as 'list' | 'kanban') || 'list';
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const isAdmin = user?.is_admin || false;

  const loadProjects = async () => {
    try {
      const data = await projectsApi.getAll(false);
      setProjects(data);
    } catch (err) {
      console.error('Failed to load projects:', err);
    }
  };

  const loadTags = async () => {
    try {
      const data = await taskTagsApi.getTree();
      setTagTree(data);
    } catch (err) {
      console.error('Failed to load tags:', err);
    }
  };

  const loadTasksByTag = async (tagId: number) => {
    try {
      setIsLoading(true);
      const data = await taskTagsApi.getTasksByTag(tagId);
      setTasks(data);
      setError('');
    } catch (err) {
      setError('Failed to load tasks by tag');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadTasks = async () => {
    try {
      setIsLoading(true);
      const projectId = projectFilter === '' ? undefined : projectFilter === '0' ? 0 : parseInt(projectFilter);
      const data = await tasksApi.getAll(
        filteredStatus || undefined,
        userFilter === 'all' ? 'all' : undefined,
        filteredStatus === 'archived',
        projectId
      );
      setTasks(data);
      setError('');
    } catch (err) {
      setError('Failed to load tasks');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTaskArchive = async (taskId: number) => {
    try {
      await tasksApi.archive(taskId);
      // Reload tasks to reflect the archived task being removed
      await loadTasks();
    } catch (err) {
      console.error('Failed to archive task:', err);
      setError('Failed to archive task');
    }
  };

  const handleTaskReorder = async (taskId: number, newStatus: TaskStatus, newPosition: number) => {
    try {
      const task = tasks.find(t => t.id === taskId);
      const statusChanged = task && task.status !== newStatus;

      // Call the reorder API with the new position and optional status
      await tasksApi.reorder(taskId, newPosition, statusChanged ? newStatus : undefined);

      // Reload tasks to reflect the new order and status
      await loadTasks();
    } catch (err) {
      console.error('Failed to reorder task:', err);
      setError('Failed to reorder task');
    }
  };

  useEffect(() => {
    loadProjects();
    loadTags();
  }, []);

  useEffect(() => {
    if (selectedTagId !== null) {
      loadTasksByTag(selectedTagId);
    } else {
      loadTasks();
    }
  }, [filteredStatus, userFilter, projectFilter, selectedTagId]);

  // Handle tag selection
  const handleSelectTag = (tagId: number | null) => {
    setSelectedTagId(tagId);
    // Clear other filters when selecting a tag
    if (tagId !== null) {
      setFilteredStatus('');
      setProjectFilter('');
    }
    // Scroll to top to show header and start of filtered list
    window.scrollTo(0, 0);
  };

  // Get selected tag path for title (returns array of tag names from root to selected)
  const getSelectedTagPath = (): string[] | null => {
    if (selectedTagId === null) return null;
    const findTagPath = (nodes: TaskTagTreeNode[], path: string[] = []): string[] | null => {
      for (const node of nodes) {
        const currentPath = [...path, node.name];
        if (node.id === selectedTagId) return currentPath;
        const found = findTagPath(node.children, currentPath);
        if (found) return found;
      }
      return null;
    };
    return findTagPath(tagTree);
  };

  // Listen for WebSocket task updates
  useEffect(() => {
    if (lastUpdate) {
      setTasks(prevTasks => {
        const index = prevTasks.findIndex(t => t.id === lastUpdate.id);
        if (index !== -1) {
          // Update existing task
          const updatedTasks = [...prevTasks];
          updatedTasks[index] = {
            ...updatedTasks[index],
            status: lastUpdate.status as TaskStatus,
            updated_at: lastUpdate.updated_at
          };
          return updatedTasks;
        } else {
          // Task not in current list, reload to get it
          loadTasks();
          return prevTasks;
        }
      });
    }
  }, [lastUpdate]);

  // Persist filters and view mode to localStorage
  useEffect(() => {
    localStorage.setItem('tasksFilteredStatus', filteredStatus);
  }, [filteredStatus]);

  useEffect(() => {
    localStorage.setItem('tasksUserFilter', userFilter);
  }, [userFilter]);

  useEffect(() => {
    localStorage.setItem('tasksProjectFilter', projectFilter);
  }, [projectFilter]);

  useEffect(() => {
    localStorage.setItem('tasksViewMode', viewMode);
  }, [viewMode]);

  if (isLoading && tasks.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Tasks - MAIA Agent 128</title>
      </Helmet>
      <div className="min-h-screen bg-gray-50">
        <Navigation />

      {/* Header and Filters - Always Constrained */}
      <div className="max-w-6xl mx-auto px-4 pt-24 pb-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">
            {selectedTagId !== null && getSelectedTagPath() ? (
              <>
                Tag:{' '}
                {getSelectedTagPath()!.map((name, index, arr) => (
                  <span key={index}>
                    {index < arr.length - 1 ? (
                      <span className="text-lg font-normal text-gray-500">{name} / </span>
                    ) : (
                      <span>{name}</span>
                    )}
                  </span>
                ))}
              </>
            ) : projectFilter && projectFilter !== '0' ? (
              <>
                {projects.find(p => p.id === parseInt(projectFilter))?.name || 'My Tasks'}
              </>
            ) : projectFilter === '0' ? (
              'Tasks Without Project'
            ) : (
              isAdmin && userFilter === 'all' ? 'All Tasks' : 'My Tasks'
            )}
          </h1>
          <div className="flex items-center space-x-3">
            <Link
              to="/task-tags"
              className="px-4 py-2 btn-secondary rounded-md text-sm font-medium"
            >
              Manage Tags
            </Link>
            <Link
              to="/tasks/new"
              className="px-4 py-2 btn-primary rounded-md text-sm font-medium"
            >
              New Task
            </Link>
          </div>
        </div>

        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center space-x-6">
            {isAdmin && (
              <div className="flex items-center space-x-2">
                <label htmlFor="user-filter" className="text-sm font-medium text-gray-700">
                  View:
                </label>
                <select
                  id="user-filter"
                  value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-200 rounded-md focus:border-black focus:ring-1 focus:ring-black"
                >
                  <option value="my">My Tasks</option>
                  <option value="all">All Users</option>
                </select>
              </div>
            )}

            <div className="flex items-center space-x-2">
              <label htmlFor="project-filter" className="text-sm font-medium text-gray-700">
                Project:
              </label>
              <select
                id="project-filter"
                value={projectFilter}
                onChange={(e) => setProjectFilter(e.target.value)}
                className="px-4 py-2 border border-gray-200 rounded-md focus:border-black focus:ring-1 focus:ring-black"
              >
                <option value="">All Projects</option>
                <option value="0">No Project</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name} ({project.task_count})
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <label htmlFor="status-filter" className="text-sm font-medium text-gray-700">
                Status:
              </label>
              <select
                id="status-filter"
                value={filteredStatus}
                onChange={(e) => setFilteredStatus(e.target.value)}
                className="px-4 py-2 border border-gray-200 rounded-md focus:border-black focus:ring-1 focus:ring-black"
              >
                <option value="">All</option>
                <option value={TaskStatus.DRAFT}>Draft</option>
                <option value={TaskStatus.PENDING}>Pending</option>
                <option value={TaskStatus.PROCESSING}>Processing</option>
                <option value={TaskStatus.COMPLETED}>Completed</option>
                <option value={TaskStatus.FAILED}>Failed</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>

          {/* View Mode Toggle */}
          <div className="flex items-center space-x-2 bg-white border border-gray-200 rounded-md p-1">
            <button
              onClick={() => setViewMode('list')}
              className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                viewMode === 'list'
                  ? 'bg-black text-white'
                  : 'text-gray-600 hover:text-black'
              }`}
            >
              List
            </button>
            <button
              onClick={() => setViewMode('kanban')}
              className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                viewMode === 'kanban'
                  ? 'bg-black text-white'
                  : 'text-gray-600 hover:text-black'
              }`}
            >
              Kanban
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}
      </div>

      {/* Content Area - Full Width for Kanban, Constrained for List */}
      {viewMode === 'kanban' ? (
        <div>
          {tasks.length === 0 ? (
            <div className="max-w-6xl mx-auto bg-white border border-gray-200 rounded-lg p-12 text-center">
              <p className="text-gray-500">No tasks yet</p>
              <Link
                to="/tasks/new"
                className="inline-block mt-4 px-4 py-2 btn-primary rounded-md text-sm font-medium"
              >
                Create Your First Task
              </Link>
            </div>
          ) : (
            <KanbanBoard
              tasks={tasks}
              isAdmin={isAdmin}
              showAllUsers={userFilter === 'all'}
              onTaskArchive={handleTaskArchive}
              onTaskReorder={handleTaskReorder}
            />
          )}
        </div>
      ) : (
        <div className="max-w-7xl mx-auto px-4 pb-8">
          <div className="flex gap-6">
            {/* Tag Tree Sidebar */}
            <div className="w-64 flex-shrink-0">
              <div
                ref={sidebarRef}
                className="bg-white border border-gray-200 rounded-lg p-4"
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-gray-900">Tags</h3>
                  {selectedTagId !== null && (
                    <button
                      onClick={() => handleSelectTag(null)}
                      className="text-xs text-gray-500 hover:text-gray-700"
                    >
                      Clear
                    </button>
                  )}
                </div>
                {tagTree.length === 0 ? (
                  <p className="text-sm text-gray-500">No tags yet</p>
                ) : (
                  <div className="space-y-0.5">
                    {tagTree.map((node) => (
                      <TagTreeNode
                        key={node.id}
                        node={node}
                        level={0}
                        selectedTagId={selectedTagId}
                        onSelectTag={handleSelectTag}
                      />
                    ))}
                  </div>
                )}
                <Link
                  to="/task-tags"
                  className="block mt-4 text-xs text-center text-gray-500 hover:text-gray-700"
                >
                  Manage Tags →
                </Link>
              </div>
            </div>

            {/* Tasks List */}
            <div className="flex-1 min-w-0">
          {tasks.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
              <p className="text-gray-500">
                {selectedTagId !== null ? 'No tasks with this tag' : filteredStatus ? 'No tasks with this status' : 'No tasks yet'}
              </p>
              {!filteredStatus && selectedTagId === null && (
                <Link
                  to="/tasks/new"
                  className="inline-block mt-4 px-4 py-2 btn-primary rounded-md text-sm font-medium"
                >
                  Create Your First Task
                </Link>
              )}
            </div>
          ) : (
            <div className="grid gap-4">
              {tasks.map((task) => (
                <Link
                  key={task.id}
                  to={`/tasks/${task.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white border border-gray-200 rounded-lg p-6 card-hover shadow-subtle"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        {task.unread && (
                          <span className="w-2.5 h-2.5 bg-blue-500 rounded-full inline-block flex-shrink-0"></span>
                        )}
                        <h3 className={`text-lg font-medium text-gray-900 ${task.unread ? 'font-semibold' : ''}`}>
                          {task.title}
                        </h3>
                      </div>
                      {isAdmin && userFilter === 'all' && task.created_by_username && (
                        <p className="text-xs text-gray-500 mt-1">
                          By: {task.created_by_fullname || task.created_by_username} (@{task.created_by_username})
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(task.status)}`}>
                        {getStatusLabel(task.status)}
                      </span>
                      {task.archived && (
                        <span className="px-3 py-1 text-xs font-medium rounded-full bg-gray-200 text-gray-700">
                          Archived
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-2 mb-4">
                    {task.description}
                  </p>
                  {/* Tags */}
                  {task.tags && task.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {task.tags.map((tag) => (
                        <span
                          key={tag.id}
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs ${
                            tag.is_own_tag
                              ? ''
                              : 'border-2 border-dashed opacity-70'
                          }`}
                          style={{
                            backgroundColor: tag.color ? `${tag.color}20` : '#f3f4f6',
                            color: tag.color || '#4b5563',
                            borderColor: !tag.is_own_tag ? (tag.color || '#9ca3af') : undefined,
                          }}
                          title={tag.is_own_tag ? tag.full_path : `Third-party: ${tag.full_path}`}
                        >
                          {tag.color && (
                            <span
                              className="w-1.5 h-1.5 rounded-full mr-1"
                              style={{ backgroundColor: tag.color }}
                            />
                          )}
                          {!tag.is_own_tag && <span className="mr-1">👤</span>}
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>Created: {new Date(task.created_at).toLocaleDateString()}</span>
                    <span>ID: {task.id}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}
